import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo from "@/components/base/Seo";
import type { Category } from "@/types/directory";
import { getCategories } from "@/services/directoryService";
import CategoryCard from "@/components/base/CategoryCard";

export default function CategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    let active = true;
    getCategories()
      .then((list) => {
        if (active) setCategories(list);
      })
      .catch(() => {
        if (active) setCategories([]);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <SiteLayout>
      <Seo
        title="All Business Categories in Coastal Karnataka"
        description="Explore every business category on Coastal Bizz Connect — restaurants, hotels, hospitals, builders, salons, home services and more across South Canara, Udupi and North Canara."
        keywords="business categories Karnataka, restaurants Mangaluru, hotels Udupi, hospitals North Canara, local services coastal Karnataka"
        path="/categories"
      />
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-10 md:py-12">
          <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="Breadcrumb">
            <span>Home</span>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="font-medium text-foreground-700">Categories</span>
          </nav>
          <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
            All business categories
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            Explore every category on Coastal Bizz Connect — from restaurants and resorts to hospitals,
            builders, salons and home services across the three coastal districts.
          </p>
          <Link
            to="/categories-a-z"
            className="mt-5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors duration-200 hover:border-accent-300 hover:text-primary-700"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-sort-alphabet-asc text-base" />
            </span>
            View all categories A to Z
          </Link>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-6 py-10 md:py-12">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((category) => (
            <CategoryCard key={category.id} category={category} />
          ))}
        </div>
      </section>
    </SiteLayout>
  );
}